import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DropdownChangeEvent } from '@volvo/vcdk';
import { DropdownOption, Tag } from '@volvo/vcdk-react';
import { Dropdown } from '@volvo/vcdk-react/Dropdown';
import { forwardRef, useImperativeHandle, useState } from 'react';

import type { RoleHandle } from './RoleHandle';
import type { IFleetAccountRole, IRoleIdentifier } from '@/service/vpc/types/IFleetAccountRole';
import type { Placement } from '@floating-ui/dom';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { getAvailableRolesQueryOptions, queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  label?: string;
  placement?: Placement;
  isEdit: boolean;
  endUserId: string;
  currentRoles: IFleetAccountRole[];
};

const FleetAccountRoles = forwardRef<RoleHandle, Props>(
  ({ label, placement = 'top', isEdit, endUserId, currentRoles }: Props, ref) => {
    // const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { fleetAccountId } = useFleetAccount();

    const [selectedRole, setSelectedRole] = useState<IRoleIdentifier[]>(
      currentRoles.filter((role) => role.role !== 'Member').map((r) => r.role) || []
    );

    const { data: availableRoles } = useQuery(getAvailableRolesQueryOptions());

    const { mutateAsync: addRoles } = useMutation({
      mutationFn: ({
        fleetAccountId,
        endUserId,
        roles,
      }: {
        fleetAccountId: string;
        endUserId: string;
        roles: string[];
      }) => vpcService.FleetAccount.assignFleetAccountRoles({ fleetAccountId, endUserId, roles }),
      onSuccess() {
        // Invalidate or refetch queries related to roles if needed
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getFleetAccountRoles],
          exact: false,
        });
      },
    });

    const { mutateAsync: removeRoles } = useMutation({
      mutationFn: ({
        fleetAccountId,
        roles,
      }: {
        fleetAccountId: string;
        endUserId: string;
        roles: IFleetAccountRole[];
      }) => vpcService.FleetAccount.removeFleetAccountRoles({ fleetAccountId, roles }),
      onSuccess() {
        // Invalidate or refetch queries related to roles if needed
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getFleetAccountRoles],
          exact: false,
        });
      },
    });

    const handleSave = async () => {
      const removedRoles = currentRoles
        ?.filter((role) => role.role !== 'Member')
        .filter((role) => !selectedRole.includes(role.role));

      const newRoles = selectedRole.filter(
        (role) =>
          !currentRoles
            ?.filter((role) => role.role !== 'Member')
            .map((r) => r.role)
            .includes(role)
      );

      if (fleetAccountId && newRoles.length) {
        await addRoles({ fleetAccountId, endUserId, roles: newRoles });
      }
      if (fleetAccountId && removedRoles.length) {
        await removeRoles({ fleetAccountId, endUserId, roles: removedRoles });
      }
    };

    // Expose the save function to parent
    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    return isEdit ? (
      <Dropdown
        multi
        disableCloseOnSelect
        size="small"
        label={label}
        placement={placement}
        valueAsArray={selectedRole}
        placeholder="Select roles"
        className="[&::part(input-wrapper)]:h-10 [&::part(input-wrapper)]:flex-nowrap"
        onChange={(e: DropdownChangeEvent) => {
          setSelectedRole(e.detail.valueAsArray.map((value) => value as IRoleIdentifier));
        }}
      >
        {availableRoles
          ?.filter((role) => role !== 'Member')
          ?.map((role, option_index) => (
            <DropdownOption key={option_index} value={role}>
              {role}
            </DropdownOption>
          ))}
      </Dropdown>
    ) : (
      <div className="flex gap-2">
        {currentRoles &&
          (currentRoles.length > 1 ? currentRoles.filter((role) => role.role !== 'Member') : currentRoles).map(
            (role) => (
              <Tag key={`${role.fleetAccountRoleId}-${role.endCustomerId}-${role.role}`} variant="neutral-subtle">
                {role.role}
              </Tag>
            )
          )}
      </div>
    );
  }
);

export default withComponentErrorBoundary(FleetAccountRoles);
